import React from 'react';
import styled, {useTheme} from 'styled-components';
import moment from 'moment';
import {Chart} from '@/web-components/core/chart/Chart';
import {EmptyState} from '../../tickets/components/EmptyState';
import {trendSeriesColors} from './dashboardChartColors';
import type {GraphColorTokens} from './dashboardChartColors';

export interface TrendPoint {
	date: string;
	created: number;
	resolved: number;
	breached: number;
}

interface Props {
	trend: TrendPoint[];
}

const Wrapper = styled.div`
	height: 100%;
	min-height: 250px;
	display: flex;
	flex-direction: column;
`;

const ChartArea = styled.div`
	position: relative;
	flex-grow: 1;
`;

/** Just the slice of the ChartJS options object `integerYAxis` rewrites. */
interface ChartOptions {
	scales?: {y?: {ticks?: Record<string, unknown>}};
}

// Ticket counts are whole numbers, so the y axis must not label half a ticket. The
// shared Chart caps the axis at 5 ticks and starts at zero, which on a small range
// (max 2) would otherwise step 0 / 0.5 / 1 / …
const integerYAxis = (options: ChartOptions): ChartOptions => ({
	...options,
	scales: {
		...options.scales,
		y: {...options.scales?.y, ticks: {...options.scales?.y?.ticks, precision: 0}},
	},
});

export function TicketTrendChart({trend}: Props) {
	const colors = trendSeriesColors(useTheme() as GraphColorTokens);

	if (trend.length === 0) {
		return (
			<Wrapper>
				<EmptyState variant="inset">No data for the selected period</EmptyState>
			</Wrapper>
		);
	}

	const toMoment = (dateStr: string) => moment(dateStr, 'YYYY-MM-DD');

	const dataSets = [
		{
			name: 'Created',
			color: colors.created,
			type: 'line',
			data: trend.map(p => ({date: toMoment(p.date), value: p.created})),
		},
		{
			name: 'Resolved',
			color: colors.resolved,
			type: 'line',
			data: trend.map(p => ({date: toMoment(p.date), value: p.resolved})),
		},
		{
			name: 'SLA breached',
			color: colors.breached,
			type: 'bar',
			data: trend.map(p => ({date: toMoment(p.date), value: p.breached})),
		},
	];

	// The shared Chart already gives the PRD's tooltip and legend: its tooltip is
	// mode 'index' / intersect false, so hovering any day shows that date plus all
	// three series, and its legend sits below the plot.
	return (
		<Wrapper>
			<ChartArea>
				<Chart dataSets={dataSets} modifyOptions={integerYAxis} />
			</ChartArea>
		</Wrapper>
	);
}
